/*
 * PlayVinyl: новий вигляд сторінки товару (налаштування секції pv_pdp).
 * <pv-product-stage> — сцена з кадрами: індекс зі «ручкою», стрілки, свайп, голка-лічильник,
 *   пакшот (білий кут кадру) лягає на папір, фото-атмосфера заповнює кадр, клік відкриває зум теми.
 * <pv-product-tabs> — вкладки з голкою; у треклисті голка йде по доріжках.
 * Ціна-одометр, «пластинка летить у кошик». Розмітка: snippets/pv-product-*.liquid, стилі: assets/pv-product.css.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pad = (n) => (n < 10 ? '0' : '') + n;

  /* ------------------------------------------------------------------ stage */
  class PvProductStage extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('[data-pvp-track]');
      this.slides = [...this.querySelectorAll('[data-pvp-slide]')];
      this.rail = [...this.querySelectorAll('[data-pvp-go]')];
      this.knob = this.querySelector('.pvp-rail__knob');
      this.needle = this.querySelector('[data-pvp-needle]');
      this.num = this.querySelector('[data-pvp-n]');
      this.cur = 0;
      if (!this.track || !this.slides.length) return;

      this.rail.forEach((b) => b.addEventListener('click', () => this.go(+b.dataset.pvpGo)));
      this.querySelectorAll('[data-pvp-step]').forEach((b) =>
        b.addEventListener('click', () => this.go(this.cur + +b.dataset.pvpStep))
      );
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.cur + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.cur - 1); }
      });
      let raf = 0;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.onScroll());
      }, { passive: true });
      window.addEventListener('resize', () => { this.fit(); this.place(this.cur, false); }, { passive: true });
      this.fit();

      /* пакшот чи фото: білий кут кадру = пакшот. Читаємо крихітну копію кадру (data-probe) з CORS —
         сам кадр і галерея теми вже в кеші без CORS, з них пікселі не прочитати */
      this.slides.forEach((slide) => {
        const img = slide.querySelector('img.pvp-img');
        if (!img) { slide.classList.add('is-photo'); return; }
        slide.addEventListener('click', () => this.zoom(slide));
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.decoding = 'async';
        probe.onload = () => slide.classList.add(this.isPackshot(probe) ? 'is-pack' : 'is-photo');
        probe.onerror = () => slide.classList.add('is-photo');
        let src = slide.dataset.probe || '';
        if (src.startsWith('//')) src = location.protocol + src;
        probe.src = src + (src.includes('?') ? '&' : '?') + 'pvp=1';
      });
      this.railBox = this.querySelector('.pvp-rail');
      if (this.railBox) {
        const fade = () => this.railFade();
        this.railBox.addEventListener('scroll', fade, { passive: true });
        if ('ResizeObserver' in window) new ResizeObserver(fade).observe(this.railBox);
        fade();
      }
      this.place(0, false);
    }

    /* кадр + лінія під ним мають влізти в екран від свого місця на сторінці */
    fit() {
      if (window.innerWidth < 750) { this.style.removeProperty('--pvp-fit'); return; }
      const canvas = this.querySelector('.pvp-canvas');
      if (!canvas) return;
      const groove = this.querySelector('.pvp-groove');
      const top = canvas.getBoundingClientRect().top + window.scrollY;
      const below = (groove ? groove.offsetHeight + 18 : 0) + 28;
      const h = Math.round(window.innerHeight - top - below);
      this.style.setProperty('--pvp-fit', Math.max(h, 420) + 'px');
    }

    railFade() {
      const r = this.railBox;
      const max = r.scrollHeight - r.clientHeight;
      r.classList.toggle('is-more-up', r.scrollTop > 4);
      r.classList.toggle('is-more-down', r.scrollTop < max - 4);
    }

    /* активна мініатюра завжди видима в індексі */
    railReveal(btn) {
      const r = this.railBox;
      if (!r || !btn || r.scrollHeight <= r.clientHeight) return;
      const top = btn.offsetTop;
      const bottom = top + btn.offsetHeight;
      const pad = 60;
      if (top - pad >= r.scrollTop && bottom + pad <= r.scrollTop + r.clientHeight) return;
      r.scrollTo({ top: top - (r.clientHeight - btn.offsetHeight) / 2, behavior: reduce.matches ? 'auto' : 'smooth' });
    }

    /* пакшот = світлий весь край кадру (рамка в 1 px крихітної копії), а не лише кути:
       у студійного знімка, де корпус майже торкається країв, кути бувають темні */
    isPackshot(img) {
      try {
        const n = 32;
        const c = document.createElement('canvas');
        c.width = n; c.height = n;
        const x = c.getContext('2d', { willReadFrequently: true });
        x.drawImage(img, 0, 0, n, n);
        const d = x.getImageData(0, 0, n, n).data;
        let light = 0, all = 0;
        for (let i = 0; i < n; i += 1) {
          [[i, 0], [i, n - 1], [0, i], [n - 1, i]].forEach(([px, py]) => {
            const k = (py * n + px) * 4;
            all += 1;
            /* прозорий край (PNG з «рамки» в адмінці) — теж фон пакшота: canvas віддає його як 0,0,0,0,
               і без перевірки альфи такий кадр ставав «фото» на весь кадр з обрізаним низом */
            if (d[k + 3] < 40 || Math.min(d[k], d[k + 1], d[k + 2]) > 226) light += 1;
          });
        }
        return light / all >= 0.72;
      } catch (e) {
        return false; /* кадр з іншого домену без CORS — показуємо як фото */
      }
    }

    go(i) {
      const n = this.slides.length;
      i = ((i % n) + n) % n;
      const left = this.slides[i].offsetLeft - this.track.offsetLeft - (this.track.clientWidth - this.slides[i].clientWidth) / 2;
      this.track.scrollTo({ left, behavior: reduce.matches ? 'auto' : 'smooth' });
      this.place(i, true);
    }

    onScroll() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const f = max > 0 ? this.track.scrollLeft / max : 0;
      const i = Math.round(f * (this.slides.length - 1));
      if (this.needle) this.needle.style.left = (f * 100).toFixed(2) + '%';
      if (i !== this.cur) this.place(i, false, true);
    }

    place(i, fromClick, fromScroll) {
      this.cur = i;
      this.rail.forEach((b, j) => {
        b.classList.toggle('is-on', j === i);
        if (j === i) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
      });
      if (this.knob && this.rail[i]) this.knob.style.transform = `translateY(${this.rail[i].offsetTop}px)`;
      if (this.railBox && i !== this.prevRail) this.railReveal(this.rail[i]);
      this.prevRail = i;
      if (this.num) this.num.textContent = pad(i + 1);
      if (this.needle && !fromScroll) {
        const n = this.slides.length;
        this.needle.style.left = (n > 1 ? (i / (n - 1)) * 100 : 0) + '%';
      }
      this.slides.forEach((s, j) => s.classList.toggle('is-on', j === i));
    }

    zoom(slide) {
      if (slide.dataset.mediaType !== 'image') return;
      const product = this.closest('product-component') || document;
      const zooms = product.querySelectorAll('zoom-images [data-zoom-image]');
      if (!zooms.length) return;
      const images = this.slides.filter((s) => s.dataset.mediaType === 'image');
      const k = images.indexOf(slide);
      if (zooms[k]) zooms[k].click();
    }
  }

  /* ------------------------------------------------------------------- tabs */
  class PvProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.tabs = [...this.querySelectorAll('[data-pvp-tab]')];
      this.panes = [...this.querySelectorAll('[data-pvp-pane]')];
      this.bar = this.querySelector('.pvp-tabs__bar');
      this.dot = this.querySelector('.pvp-tabs__needle');
      this.tabs.forEach((t) => t.addEventListener('click', () => this.show(+t.dataset.pvpTab)));
      this.addEventListener('keydown', (e) => {
        if (!e.target.matches('[data-pvp-tab]')) return;
        const i = this.tabs.indexOf(e.target);
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const j = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.show(j); this.tabs[j].focus();
        }
      });
      const first = this.tabs.findIndex((t) => t.classList.contains('is-on'));
      requestAnimationFrame(() => this.move(Math.max(0, first)));
      window.addEventListener('resize', () => this.move(this.active || 0), { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.move(this.active || 0));
      this.tracks();
    }

    show(i) {
      this.tabs.forEach((t, j) => { t.classList.toggle('is-on', j === i); t.setAttribute('aria-selected', j === i ? 'true' : 'false'); });
      this.panes.forEach((p) => p.classList.toggle('is-on', +p.dataset.pvpPane === i));
      this.move(i);
    }

    move(i) {
      this.active = i;
      const t = this.tabs[i];
      if (!t) return;
      if (this.bar) { this.bar.style.width = t.offsetWidth + 'px'; this.bar.style.transform = `translateX(${t.offsetLeft}px)`; }
      if (this.dot) this.dot.style.transform = `translateX(${t.offsetLeft + t.offsetWidth / 2 - 4.5}px)`;
    }

    /* треклист: голка йде по доріжках, поки вкладка на екрані */
    tracks() {
      const sides = [...this.querySelectorAll('[data-pvp-side]')];
      if (!sides.length) return;
      const rows = sides.flatMap((s) => [...s.querySelectorAll('li')].map((li) => [s, li]));
      let k = 0;
      let timer = 0;
      const step = () => {
        rows.forEach(([, li]) => li.classList.remove('is-on'));
        sides.forEach((s) => s.classList.remove('is-live'));
        const [s, li] = rows[k];
        if (!li.offsetParent) return;
        li.classList.add('is-on'); s.classList.add('is-live');
        const nd = s.querySelector('.pvp-side__needle');
        if (nd) nd.style.top = (li.offsetTop + li.offsetHeight / 2) + 'px';
        k = (k + 1) % rows.length;
      };
      const io = new IntersectionObserver(([e]) => {
        clearInterval(timer);
        if (e.isIntersecting) { step(); if (!reduce.matches) timer = setInterval(step, 2600); }
      }, { threshold: 0.2 });
      io.observe(sides[0].closest('.pvp-tracks') || sides[0]);
    }
  }

  /* ---------------------------------------------------------------- odometer */
  // Сума з будь-якого формату магазину: «₴88,045.05», «88 045,05 ₴», «1.234,56».
  // Копійки — останній розділювач із 1–2 цифрами після нього; решта
  // розділювачів — тисячі. Раніше копійки зливались із гривнями.
  function parseMoney(raw) {
    const num = ((raw || '').match(/\d[\d\s.,'’  ]*/) || [''])[0].replace(/[\s.,'’  ]+$/, '');
    const dec = num.match(/[.,](\d{1,2})$/);
    const whole = (dec ? num.slice(0, -dec[0].length) : num).replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    const cents = dec && Number(dec[1]) ? dec[1].padEnd(2, '0') : '';
    return { whole, cents, txt: whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') };
  }

  // Стара (закреслена) ціна — у тому ж форматі, що й нова: «92 679 ₴», а не «₴92,679.00».
  // theme.js переписує її при зміні варіанта — спостерігач форматує знову.
  function strikePrice(root) {
    const el = root.querySelector('.product__price [data-compare-price]');
    if (!el) return;
    const fmt = () => {
      const raw = (el.textContent || '').trim();
      const m = parseMoney(raw);
      if (!m.whole) return;
      const out = `${m.txt}${m.cents ? `,${m.cents}` : ''} ₴`;
      if (raw !== out) el.textContent = out;
    };
    fmt();
    new MutationObserver(fmt).observe(el, { childList: true, characterData: true, subtree: true });
  }

  function odometer(root) {
    const src = root.querySelector('.product__price [data-product-price]');
    if (!src) return;
    const wrap = document.createElement('span');
    wrap.className = 'pvp-odo';
    wrap.setAttribute('aria-hidden', 'true');
    src.after(wrap);
    src.classList.add('pvp-odo-src');
    let last = '';
    const render = () => {
      const { whole, cents, txt } = parseMoney((src.textContent || '').trim());
      if (!whole) { wrap.hidden = true; src.classList.remove('pvp-odo-src'); return; }
      wrap.hidden = false; src.classList.add('pvp-odo-src');
      if (txt + cents === last) return;
      last = txt + cents;
      wrap.innerHTML = txt.split('').map((ch, i) => (ch === ' '
        ? '<span class="pvp-odo__sp"></span>'
        : `<span class="pvp-odo__col" style="--i:${i}" data-d="${ch}">${'0123456789'.split('').map((d) => `<span>${d}</span>`).join('')}</span>`)).join('')
        + (cents ? `<span class="pvp-odo__cents">,${cents}</span>` : '')
        + '<span class="pvp-odo__cur">₴</span>';
      const cols = wrap.querySelectorAll('.pvp-odo__col');
      if (reduce.matches) cols.forEach((c) => { c.style.transition = 'none'; c.style.transform = `translateY(-${c.dataset.d}em)`; });
      else requestAnimationFrame(() => requestAnimationFrame(() => cols.forEach((c) => { c.style.transform = `translateY(-${c.dataset.d}em)`; })));
    };
    render();
    new MutationObserver(render).observe(src, { childList: true, characterData: true, subtree: true });
  }

  /* -------------------------------------------------------- disc to the cart */
  function flyToCart(root) {
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn || btn.disabled || reduce.matches) return;
      const carts = [...document.querySelectorAll('.navlink--cart, cart-count')].filter((el) => el.offsetParent);
      const cart = carts[0];
      if (!cart || !document.body.animate) return;
      const a = btn.getBoundingClientRect();
      const z = cart.getBoundingClientRect();
      const fly = document.createElement('i');
      fly.className = 'pvp-fly';
      fly.style.left = (a.left + a.width / 2 - 16) + 'px';
      fly.style.top = (a.top + a.height / 2 - 16) + 'px';
      document.body.appendChild(fly);
      const dx = z.left + z.width / 2 - (a.left + a.width / 2);
      const dy = z.top + z.height / 2 - (a.top + a.height / 2);
      fly.animate([
        { transform: 'translate(0,0) scale(1) rotate(0)', opacity: 1 },
        { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 110}px) scale(1.1) rotate(360deg)`, opacity: 1, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(.35) rotate(720deg)`, opacity: 0.6 }
      ], { duration: 850, easing: 'cubic-bezier(.5,0,.3,1)' }).onfinish = () => fly.remove();
    });
  }

  function initSwatches(root) {
    root.querySelectorAll('[data-pvp-sw]').forEach((box) => {
      const track = box.querySelector('[data-pvp-sw-track]');
      if (!track) return;
      const prev = box.querySelector('[data-pvp-sw-step="-1"]');
      const next = box.querySelector('[data-pvp-sw-step="1"]');
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth;
        const left = track.scrollLeft > 4;
        const right = track.scrollLeft < max - 4;
        box.classList.toggle('is-more-left', left);
        box.classList.toggle('is-more-right', right);
        if (prev) prev.hidden = !left;
        if (next) next.hidden = !right;
      };
      box.querySelectorAll('[data-pvp-sw-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const dir = Number(btn.dataset.pvpSwStep);
          track.scrollBy({ left: dir * Math.max(track.clientWidth * 0.8, 120), behavior: 'smooth' });
        });
      });
      track.addEventListener('scroll', sync, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
      sync();
    });
  }

  /* ------------------------------------------------ description: photos aside */
  // Фото з опису — окремою колонкою праворуч, текст ліворуч. Раніше опис ділився
  // на дві CSS-колонки, але в частини товарів увесь опис — один <p> з <br>,
  // а фото всередині нього: абзац не розривається, і фото опинялось під текстом
  // у лівій колонці, права пустувала. Тепер розкладка не залежить від HTML опису.
  function descMedia(root) {
    root.querySelectorAll('.pvp-desc').forEach((desc) => {
      if (desc.dataset.pvpMedia) return;
      desc.dataset.pvpMedia = '1';
      const imgs = [...desc.querySelectorAll('img')];
      if (!imgs.length) return;
      const aside = document.createElement('div');
      aside.className = 'pvp-desc__media';
      imgs.forEach((img) => {
        // піднімаємось до обгортки, у якій немає нічого, крім цього фото (<p><img></p>, <a><img></a>)
        let el = img;
        while (el.parentElement && el.parentElement !== desc
          && !el.parentElement.textContent.trim()
          && el.parentElement.querySelectorAll('img').length === 1) el = el.parentElement;
        aside.append(el);
      });
      // після фото лишаються порожні абзаци та зайві <br> у кінці абзаців
      desc.querySelectorAll('p, div, span, a').forEach((el) => {
        if (!el.textContent.trim() && !el.querySelector('img, iframe, video, table')) el.remove();
      });
      desc.querySelectorAll('br').forEach((br) => {
        let n = br.nextSibling;
        while (n && n.nodeType === 3 && !n.textContent.trim()) n = n.nextSibling;
        if (!n) br.remove();
      });
      const text = document.createElement('div');
      text.className = 'pvp-desc__text';
      text.append(...desc.childNodes);
      desc.append(text, aside);
      desc.classList.add('has-media');
    });
  }

  function init() {
    document.querySelectorAll('.pv-pdp').forEach((root) => {
      if (root.dataset.pvpReady) return;
      root.dataset.pvpReady = '1';
      odometer(root);
      strikePrice(root);
      flyToCart(root);
      initSwatches(root);
      descMedia(root);
    });
  }

  if (!customElements.get('pv-product-stage')) customElements.define('pv-product-stage', PvProductStage);
  if (!customElements.get('pv-product-tabs')) customElements.define('pv-product-tabs', PvProductTabs);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
